/**
 * SingleDocumentOverviewPage — Overview/management page for single-document artifacts.
 *
 * Mirrors ArtifactDetailPage for multi-section documents but without outline/sections table.
 * Shows a metadata card with collaboration status, owner, submission links, word count,
 * review flow, export, and finalize/reopen.
 */

import { useEffect, useState, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Plus,
  Loader2,
  AlertCircle,
  FileEdit,
  Check,
  Lock,
  Unlock,
  X,
} from "lucide-react"
import { useResponsesStore } from "@/stores/responses-store"
import { useDraftingStore } from "@/stores/drafting-store"
import { useSectionAssignmentsStore, type CollaborationStatus } from "@/stores/section-assignments-store"
import { useAuthStore } from "@/stores/auth-store"
import { useBidReadOnly, useIsViewOnly } from "@/hooks/useViewOnly"
import { routeTemplateExportError } from "@/stores/export-templates-store"
import { usePdfExportStore } from "@/stores/pdf-export-store"
import { ExportDropdown } from "@/components/ExportDropdown"
import { exportArtifactToDocx, api, getArtifactSubmissionLinks, toastApiError } from "@/lib/api"
import type { ArtifactSubmissionLink, ExportOptions, SectionStatus } from "@/lib/api"
import { collaborationStatusConfig, tableWordCount, type CollaborationStatusKey, COLLABORATION_STATUSES, PENDING_SUGGESTION_STATUS } from "@/lib/section-status"
import { cn } from "@/lib/utils"
import { getInitials, getAvatarColor } from "@/lib/avatar"
import { SubmissionLinksIndicator } from "@/components/extraction/SubmissionLinksIndicator"
import { ArtifactOwnerSelector } from "@/components/ui/owner-assignment"
import { ArtifactPageHeader } from "@/components/ArtifactPageHeader"
import { DOCUMENT_SECTION_KEY } from "@/types/scope"
import { BriefButton } from "@/components/drafting/BriefButton"
import { BriefModal } from "@/components/drafting/BriefModal"
import { ExportGapConfirmDialog } from "@/components/drafting/ExportGapConfirmDialog"
import { DocumentQuestionsList } from "@/components/DocumentQuestionsList"
import { DraftDocumentButton } from "@/components/drafting/DraftAllSections"
import { PanelDocumentJob } from "@/components/drafting/PanelDocumentJob"
import { useDraftDocument } from "@/hooks/useDraftDocument"
import { useExportGapCheck } from "@/hooks/useExportGapCheck"
import { useCoverageStore } from "@/stores/coverage-store"
import { toast } from "sonner"
import { toastExportResult } from '@/lib/pending-ai-change-notices'

// ===================
// Shared Types
// ===================

interface BidMemberInfo {
  userId: string
  role: string
  email: string
  fullName: string | null
  avatarUrl: string | null
}

// ===================
// Member Avatar
// ===================

function MemberAvatar({ user, size = "sm" }: {
  user: { userId: string; fullName: string | null; avatarUrl: string | null }
  size?: "sm" | "md"
}) {
  const sizeClasses = size === "sm" ? "h-6 w-6" : "h-7 w-7"
  const textClasses = size === "sm" ? "text-[10px]" : "text-xs"
  const color = getAvatarColor(user.userId)

  if (user.avatarUrl) {
    return (
      <img
        src={user.avatarUrl}
        alt={user.fullName ?? "User"}
        className={cn("rounded-full object-cover shrink-0", sizeClasses)}
      />
    )
  }

  return (
    <div
      className={cn(
        "rounded-full flex items-center justify-center font-medium shrink-0",
        color.bg, color.text, sizeClasses, textClasses
      )}
    >
      {getInitials(user.fullName)}
    </div>
  )
}

// ===================
// Document Writer Cell (compact avatar — matches SectionOwnerCell)
// ===================

interface DocumentWriterCellProps {
  assignment: { userId: string; fullName: string | null; avatarUrl: string | null } | undefined
  bidMembers: BidMemberInfo[]
  canAssign: boolean
  currentUserId: string | null | undefined
  onAssign: (userId: string) => void
  onUnassign: () => void
  isFinalized: boolean
}

function DocumentWriterCell({
  assignment,
  bidMembers,
  canAssign,
  currentUserId,
  onAssign,
  onUnassign,
  isFinalized,
}: DocumentWriterCellProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const hasAssignment = !!assignment

  const canSelfClaim = !canAssign && !hasAssignment && !isFinalized

  const filteredMembers = bidMembers.filter((m) => {
    if (!search) return true
    const q = search.toLowerCase()
    return m.fullName?.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
  })

  const handleAssign = (userId: string) => {
    onAssign(userId)
    setOpen(false)
    setSearch("")
  }

  const handleUnassign = () => {
    onUnassign()
    setOpen(false)
    setSearch("")
  }

  // Read-only states
  if (isFinalized && !hasAssignment) return <span className="text-meta">—</span>
  if (isFinalized && hasAssignment) {
    return (
      <div title={assignment.fullName ?? undefined}>
        <MemberAvatar user={{ userId: assignment.userId, fullName: assignment.fullName, avatarUrl: assignment.avatarUrl }} />
      </div>
    )
  }

  if (canSelfClaim) {
    return (
      <button
        onClick={() => currentUserId && onAssign(currentUserId)}
        className="h-6 w-6 rounded-full border border-dashed border-muted-foreground/30 flex items-center justify-center hover:border-muted-foreground/60 hover:bg-muted/50 transition-colors"
        title="Claim this document"
      >
        <Plus className="h-3 w-3 text-subtle" />
      </button>
    )
  }

  if (!canAssign && hasAssignment) {
    return (
      <div title={assignment.fullName ?? undefined}>
        <MemberAvatar user={{ userId: assignment.userId, fullName: assignment.fullName, avatarUrl: assignment.avatarUrl }} />
      </div>
    )
  }

  if (!canAssign) {
    return <span className="text-meta">—</span>
  }

  // Full assignment popover
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch("") }}>
      <PopoverTrigger asChild>
        {hasAssignment ? (
          <button
            className="rounded-full hover:ring-2 hover:ring-muted transition-all"
            title={`${assignment.fullName ?? "Assigned"} — click to change`}
          >
            <MemberAvatar user={{ userId: assignment.userId, fullName: assignment.fullName, avatarUrl: assignment.avatarUrl }} />
          </button>
        ) : (
          <button
            className="h-6 w-6 rounded-full border border-dashed border-muted-foreground/30 flex items-center justify-center hover:border-muted-foreground/60 hover:bg-muted/50 transition-colors"
            title="Assign writer"
          >
            <Plus className="h-3 w-3 text-subtle" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0">
        <div className="p-2 border-b border-border">
          <Input
            placeholder="Search members..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs"
          />
        </div>
        <div className="max-h-48 overflow-y-auto p-1">
          {hasAssignment && (
            <button
              onClick={handleUnassign}
              className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors text-muted-foreground"
            >
              <X className="h-3.5 w-3.5" />
              <span>Unassign</span>
            </button>
          )}
          {filteredMembers.length === 0 && (
            <div className="px-3 py-4 text-center text-meta">No members found</div>
          )}
          {filteredMembers.map((member) => {
            const isSelected = member.userId === assignment?.userId
            return (
              <button
                key={member.userId}
                onClick={() => handleAssign(member.userId)}
                className={cn(
                  "flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors",
                  isSelected && "bg-muted/50"
                )}
              >
                <MemberAvatar user={member} />
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm truncate">{member.fullName || member.email}</div>
                  {member.fullName && (
                    <div className="text-meta truncate">{member.email}</div>
                  )}
                </div>
                {isSelected && <Check className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ===================
// Review Action Buttons
// ===================

function ReviewActions({
  collaborationStatus,
  reviewerId,
  bidMembers,
  currentUserId,
  isFinalized,
  onRequestReview,
  onSubmitReview,
}: {
  collaborationStatus: CollaborationStatusKey
  reviewerId: string | null
  bidMembers: BidMemberInfo[]
  currentUserId: string | null | undefined
  isFinalized: boolean
  onRequestReview: (reviewerId: string) => void
  onSubmitReview: (decision: 'approved' | 'changes_requested', comment?: string) => void
}) {
  const [showReviewerPicker, setShowReviewerPicker] = useState(false)
  const [search, setSearch] = useState("")

  if (isFinalized) return null

  const isReviewer = reviewerId === currentUserId

  // Show "Request Review" when draft is complete
  if (collaborationStatus === 'draft_complete') {
    const filteredMembers = bidMembers.filter((m) => {
      if (m.userId === currentUserId) return false
      if (!search) return true
      const q = search.toLowerCase()
      return m.fullName?.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
    })

    return (
      <Popover open={showReviewerPicker} onOpenChange={(v) => { setShowReviewerPicker(v); if (!v) setSearch("") }}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            Request Review
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-0">
          <div className="p-2 border-b border-border">
            <Input
              placeholder="Search members..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 text-xs"
            />
          </div>
          <div className="max-h-48 overflow-y-auto p-1">
            {filteredMembers.length === 0 && (
              <div className="px-3 py-4 text-center text-meta">No other members</div>
            )}
            {filteredMembers.map((member) => (
              <button
                key={member.userId}
                onClick={() => {
                  onRequestReview(member.userId)
                  setShowReviewerPicker(false)
                  setSearch("")
                }}
                className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors"
              >
                <MemberAvatar user={member} />
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm truncate">{member.fullName || member.email}</div>
                </div>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  // Show approve / request changes when in review and current user is reviewer
  if (collaborationStatus === 'in_review' && isReviewer) {
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onSubmitReview('changes_requested')}
          className="hover:text-destructive"
        >
          Request Changes
        </Button>
        <Button
          size="sm"
          onClick={() => onSubmitReview('approved')}
        >
          <Check className="h-4 w-4 mr-1" />
          Approve
        </Button>
      </div>
    )
  }

  // Show waiting badge when in review but not the reviewer
  if (collaborationStatus === 'in_review') {
    const reviewer = bidMembers.find((m) => m.userId === reviewerId)
    return (
      <span className="text-meta">
        Waiting for review{reviewer ? ` from ${reviewer.fullName || reviewer.email}` : ''}
      </span>
    )
  }

  return null
}

// ===================
// Helper
// ===================

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—"
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return "—"
  }
}

// ===================
// Main Component
// ===================

export function SingleDocumentOverviewPage() {
  const { bidId, artifactId } = useParams<{ bidId: string; artifactId: string }>()
  const navigate = useNavigate()

  // Store state
  const {
    selectedArtifact: artifact,
    selectedArtifactPendingSuggestions,
    isLoading: artifactLoading,
    error: artifactError,
    fetchArtifactById,
    refreshPendingSuggestions,
    clearError: clearArtifactError,
    updateArtifact,
    reopenArtifact,
  } = useResponsesStore()

  const {
    outline,
    sectionStatuses,
    fetchOutline,
    fetchSectionStatuses,
  } = useDraftingStore()

  const {
    assignments,
    fetchAssignments,
    assignSection,
    unassignSection,
    updateCollaborationStatus,
    requestReview,
    submitReview,
  } = useSectionAssignmentsStore()

  const currentUserId = useAuthStore((s) => s.user?.id)

  // Bid members
  const [bidMembers, setBidMembers] = useState<BidMemberInfo[]>([])
  const isBidOwner = bidMembers.some((m) => m.userId === currentUserId && m.role === 'owner')
  const isArtifactOwner = artifact?.ownerId === currentUserId
  // A read-only user — an org viewer, or a member who is not on the bid —
  // reads the document's state; every change is refused them.
  const { isReadOnly } = useBidReadOnly(bidId)
  // Export is refused an org viewer only: a member who is not on the bid may
  // still export what they can read.
  const isViewOnly = useIsViewOnly()
  const canAssign = !isReadOnly && (isBidOwner || isArtifactOwner)

  // Lifecycle state — direct finalize was removed (see ReviewStatusBanner for the new flow).
  const isFinalized = artifact?.status === 'finalized'

  // "Draft document" (R1): offered only while the server's whole-document job
  // is switched on (it says so in the status). Single documents had no draft
  // button before, so with the switch off the page is unchanged.
  const draftDocument = useDraftDocument(artifactId, {
    enabled: artifact?.id === artifactId && artifact?.artifactType === 'narrative_document',
    // The job saved text: re-read the document row's word count. Re-reads
    // only — the document's status is left alone. It may have left a
    // suggestion for review: re-read the waiting suggestions too.
    onJobFinished: () => {
      if (!artifactId) return
      void fetchSectionStatuses(artifactId)
      void fetchAssignments(artifactId)
      void refreshPendingSuggestions(artifactId)
    },
  })
  const showDraftDocument = draftDocument.available === true
  // One filled button per screen: Start drafting steps down to secondary only
  // while Draft document renders beside it (same condition as the header).
  const isDraftDocumentInHeader =
    showDraftDocument && !isReadOnly && !isFinalized && artifact?.status !== 'pending_review'
  const [showReopenDialog, setShowReopenDialog] = useState(false)
  const [isReopening, setIsReopening] = useState(false)

  // Brief modal — opened from the document overview header.
  const [briefOpen, setBriefOpen] = useState(false)
  const loadCoverage = useCoverageStore((s) => s.load)

  // Export state
  const [isExporting, setIsExporting] = useState(false)

  // Submission links
  const [submissionLinks, setSubmissionLinks] = useState<ArtifactSubmissionLink[]>([])

  // Fetch data on mount
  useEffect(() => {
    if (bidId) void loadCoverage(bidId)
    if (artifactId) {
      void fetchArtifactById(artifactId)
      void fetchOutline(artifactId)
      void fetchSectionStatuses(artifactId)
      void fetchAssignments(artifactId)
      void getArtifactSubmissionLinks(artifactId)
        .then((data) => setSubmissionLinks(data.links))
        .catch(() => setSubmissionLinks([]))
    }
  }, [artifactId, bidId, loadCoverage, fetchArtifactById, fetchOutline, fetchSectionStatuses, fetchAssignments])

  // Fetch bid members
  useEffect(() => {
    if (bidId) {
      void api.get<{ members: BidMemberInfo[] }>(`/bids/${bidId}/members`)
        .then((data) => setBidMembers(data.members))
        .catch(() => { /* non-critical */ })
    }
  }, [bidId])

  // Document assignment (for the __document__ virtual section)
  const documentAssignment = useMemo(() => {
    return assignments.find((a) => a.sectionId === DOCUMENT_SECTION_KEY)
  }, [assignments])

  const collaborationStatus: CollaborationStatusKey =
    (documentAssignment?.collaborationStatus as CollaborationStatusKey) ?? 'not_started'
  const statusInfo = collaborationStatusConfig[collaborationStatus]

  // Section status for word count
  const documentStatus: SectionStatus | undefined = sectionStatuses.find(
    (s) => s.sectionKey === DOCUMENT_SECTION_KEY
  )

  // AI suggestions waiting on this document (null until read; loaded with the
  // artifact). While one waits, the table says so in place of the status —
  // display only, the status itself is untouched.
  const pendingSuggestionItems = selectedArtifactPendingSuggestions && selectedArtifactPendingSuggestions.artifactId === artifactId
    ? selectedArtifactPendingSuggestions.items
    : null
  const pendingSuggestion = pendingSuggestionItems
    ? pendingSuggestionItems.find((item) => item.sectionKey === DOCUMENT_SECTION_KEY) ?? pendingSuggestionItems[0] ?? null
    : null
  const documentWordCount = tableWordCount(documentStatus?.wordCount, pendingSuggestion)

  // Handlers
  const handleSetArtifactOwner = async (userId: string | null) => {
    if (!artifact?.id) return
    try {
      await updateArtifact(artifact.id, { ownerId: userId })
      toast.success(userId ? "Document owner updated" : "Document owner removed")
    } catch {
      toast.error("Failed to update document owner")
    }
  }

  const handleStatusChange = async (statusValue: string) => {
    if (!artifactId) return
    try {
      await updateCollaborationStatus(artifactId, DOCUMENT_SECTION_KEY, statusValue as CollaborationStatus)
    } catch (err) {
      toastApiError(err, 'Failed to update status')
    }
  }

  const handleAssignWriter = async (userId: string) => {
    if (!artifactId) return
    const isSelf = userId === currentUserId
    const member = bidMembers.find((m) => m.userId === userId)
    const name = member?.fullName || member?.email || "User"
    try {
      await assignSection(artifactId, DOCUMENT_SECTION_KEY, userId)
      toast(isSelf ? "Document assigned to you" : `Document assigned to ${name}`, {
        action: {
          label: "Undo",
          onClick: () => void unassignSection(artifactId, DOCUMENT_SECTION_KEY),
        },
      })
    } catch {
      toast.error("Failed to assign writer")
    }
  }

  const handleUnassignWriter = async () => {
    if (!artifactId) return
    try {
      await unassignSection(artifactId, DOCUMENT_SECTION_KEY)
      toast("Writer unassigned")
    } catch {
      toast.error("Failed to unassign writer")
    }
  }

  const handleRequestReview = async (reviewerId: string) => {
    if (!artifactId) return
    try {
      await requestReview(artifactId, DOCUMENT_SECTION_KEY, reviewerId)
      const reviewer = bidMembers.find((m) => m.userId === reviewerId)
      toast.success(`Review requested from ${reviewer?.fullName || reviewer?.email || 'reviewer'}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to request review')
    }
  }

  const handleSubmitReview = async (decision: 'approved' | 'changes_requested', comment?: string) => {
    if (!artifactId) return
    try {
      await submitReview(artifactId, DOCUMENT_SECTION_KEY, decision, comment)
      toast.success(decision === 'approved' ? 'Document approved' : 'Changes requested')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit review')
    }
  }

  const runExport = async (templateId?: string, options?: ExportOptions) => {
    if (!artifactId) return
    setIsExporting(true)
    try {
      // R3: a PDF is converted from the export's exact Word file; the export
      // menu shows its progress and result.
      const isPdf = options?.format === 'pdf'
      const { pendingAiChanges } = isPdf
        ? await usePdfExportStore.getState().exportPdf(artifactId, templateId, options)
        : await exportArtifactToDocx(artifactId, undefined, templateId, options)
      toastExportResult(isPdf ? 'PDF exported successfully' : 'Document exported successfully', pendingAiChanges)
    } catch (err) {
      // A template that would replace its own pages asks first (D4) — for a
      // PDF exactly as for Word, so the retry keeps the format.
      const retry = () => void runExport(templateId, { ...options, acknowledgeTemplateLoss: true })
      if (!routeTemplateExportError(err, retry)) toastApiError(err, 'Export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  // Spec 19: hold the export while the draft still shows evidence gaps, so the
  // strip is never silent. A single-document artifact reports under the
  // `__document__` key, which has no outline entry — the fallback title names it
  // and "Review gaps" goes to the one editor route this page has.
  const { runWithGapCheck, exportGapDialogProps } = useExportGapCheck({
    artifactId,
    outlineSections: outline?.sections ?? [],
    fallbackTitle: artifact?.name ?? 'This document',
    onReviewGaps: () => {
      void navigate(`/app/bids/${bidId}/responses/${artifactId}/document`)
    },
  })

  const handleExport = async (templateId?: string, options?: ExportOptions) => {
    if (!artifactId) return
    setIsExporting(true)
    try {
      await runWithGapCheck(() => runExport(templateId, options))
    } finally {
      setIsExporting(false)
    }
  }

  const handleReopen = async () => {
    if (!artifactId) return
    try {
      setIsReopening(true)
      await reopenArtifact(artifactId)
      setShowReopenDialog(false)
      toast.success('Document reopened')
    } catch {
      toast.error('Failed to reopen document')
    } finally {
      setIsReopening(false)
    }
  }

  const handleStartDrafting = () => {
    void navigate(`/app/bids/${bidId}/responses/${artifactId}/document`)
  }

  // Loading state
  if (artifactLoading && !artifact) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (artifactError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">{artifactError}</p>
        <Button
          variant="outline"
          onClick={() => clearArtifactError()}
        >
          Try Again
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <ArtifactPageHeader
        title={artifact?.name ?? 'Loading...'}
        description={artifact?.description ?? null}
        onBack={() => void navigate(`/app/bids/${bidId}/write`)}
        owner={
          artifact ? (
            <ArtifactOwnerSelector
              artifact={artifact}
              bidMembers={bidMembers}
              canSetOwner={isBidOwner && !isReadOnly}
              onSetOwner={(userId) => void handleSetArtifactOwner(userId)}
              compact
            />
          ) : undefined
        }
        titleBadges={
          isFinalized ? (
            <Badge variant="success" className="text-xs">
              <Lock className="h-3 w-3 mr-1" />
              Finalised
            </Badge>
          ) : undefined
        }
        actions={
          <>
            {/* Direct Finalize button removed — finalize now routes through the
                doc-level review workflow. Single-document pages will get a
                Submit-for-review CTA in a follow-up; for now use the response
                outline (ArtifactDetailPage) to submit for review. */}
            {isFinalized && !isReadOnly && (isBidOwner || isArtifactOwner) && (
              <Button variant="ghost" size="sm" onClick={() => setShowReopenDialog(true)}>
                <Unlock className="h-4 w-4 mr-2" />
                Reopen
              </Button>
            )}
            <BriefButton
              bidId={bidId ?? ''}
              artifactId={artifactId ?? ''}
              sectionKey={DOCUMENT_SECTION_KEY}
              showCounts={false}
              onOpen={() => setBriefOpen(true)}
            />
            {/* Export is a POST, which the backend refuses a view-only user. */}
            {!isViewOnly && (
              <ExportDropdown
                artifactId={artifactId}
                disabled={isExporting}
                isExporting={isExporting}
                onExport={handleExport}
              />
            )}
            {isDraftDocumentInHeader && (
              <DraftDocumentButton draftDocument={draftDocument} />
            )}
            {!isFinalized && !isReadOnly && (
              <Button
                variant={isDraftDocumentInHeader ? "secondary" : "default"}
                size="sm"
                onClick={handleStartDrafting}
              >
                <FileEdit className="h-4 w-4 mr-2" />
                Start drafting
              </Button>
            )}
          </>
        }
      />

      {/* Everything under the header scrolls as one page. The job card (its
          recap can be long) sits above the details and the questions, so a
          short window still reaches "Questions for this document" (UX2 05a). */}
      <div data-testid="document-overview-scroll" className="flex-1 overflow-auto px-6 pt-1 pb-4">
        {showDraftDocument && artifactId && (
          <PanelDocumentJob artifactId={artifactId} overview pendingSuggestions={pendingSuggestionItems} className="mt-2 mb-1" />
        )}

        {/* Document Details Table */}
        <div className="border rounded-lg overflow-hidden bg-card shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-3 py-3 text-left text-sm font-medium text-muted-foreground">
                  Document
                </th>
                <th className="w-32 px-3 py-3 text-left text-sm font-medium text-muted-foreground">
                  Status
                </th>
                <th className="w-10 px-3 py-3 text-left text-sm font-medium text-muted-foreground">
                  Writer
                </th>
                <th className="w-32 px-3 py-3 text-left text-sm font-medium text-muted-foreground">
                  Submission
                </th>
                <th className="w-24 px-3 py-3 text-left text-sm font-medium text-muted-foreground">
                  Words
                </th>
                <th className="w-40 px-3 py-3 text-left text-sm font-medium text-muted-foreground">
                  Updated
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {/* Document Title */}
                <td className="px-3 py-2">
                  <button
                    className="text-sm text-left hover:text-[hsl(var(--brand))] transition-colors"
                    onClick={handleStartDrafting}
                  >
                    {artifact?.name ?? 'Document'}
                  </button>
                </td>

                {/* Status */}
                <td className="w-32 px-3 py-2">
                  {isFinalized ? (
                    <Badge variant="success" dot={false} className="text-xs">
                      <Lock className="h-3 w-3" />
                      Finalised
                    </Badge>
                  ) : isReadOnly ? (
                    <Badge
                      variant={pendingSuggestion ? PENDING_SUGGESTION_STATUS.variant : statusInfo.variant}
                      dot
                      className="text-xs whitespace-nowrap"
                    >
                      {pendingSuggestion ? PENDING_SUGGESTION_STATUS.label : statusInfo.label}
                    </Badge>
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="cursor-pointer">
                          <Badge
                            variant={pendingSuggestion ? PENDING_SUGGESTION_STATUS.variant : statusInfo.variant}
                            dot
                            className="text-xs whitespace-nowrap hover:opacity-80 transition-opacity"
                          >
                            {pendingSuggestion ? PENDING_SUGGESTION_STATUS.label : statusInfo.label}
                          </Badge>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-44 p-1" align="start">
                        {COLLABORATION_STATUSES.map((s) => (
                          <button
                            key={s.key}
                            onClick={() => void handleStatusChange(s.key)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            <div className={cn('h-2 w-2 rounded-full', s.dotColor)} />
                            <span className="flex-1 text-left">{s.label}</span>
                            {collaborationStatus === s.key && <Check className="h-3.5 w-3.5 text-muted-foreground" />}
                          </button>
                        ))}
                      </PopoverContent>
                    </Popover>
                  )}
                </td>

                {/* Writer */}
                <td className="w-10 px-3 py-2">
                  <DocumentWriterCell
                    assignment={documentAssignment ? {
                      userId: documentAssignment.userId!,
                      fullName: documentAssignment.fullName ?? bidMembers.find((m) => m.userId === documentAssignment.userId)?.fullName ?? null,
                      avatarUrl: documentAssignment.avatarUrl ?? bidMembers.find((m) => m.userId === documentAssignment.userId)?.avatarUrl ?? null,
                    } : undefined}
                    bidMembers={bidMembers}
                    canAssign={canAssign}
                    currentUserId={currentUserId}
                    onAssign={(userId) => void handleAssignWriter(userId)}
                    onUnassign={() => void handleUnassignWriter()}
                    isFinalized={isFinalized || isReadOnly}
                  />
                </td>

                {/* Submission */}
                <td className="w-32 px-3 py-2">
                  <SubmissionLinksIndicator
                    bidId={bidId ?? ''}
                    artifactId={artifactId ?? ''}
                    sectionKey={null}
                    links={submissionLinks.filter((link) => link.sectionKey === null)}
                    onLinked={async () => {
                      if (!artifactId) return
                      const data = await getArtifactSubmissionLinks(artifactId)
                      setSubmissionLinks(data.links)
                    }}
                  />
                </td>

                {/* Words */}
                <td className="w-24 px-3 py-2 text-sm text-muted-foreground">
                  {documentWordCount != null ? documentWordCount.toLocaleString() : '—'}
                </td>

                {/* Updated */}
                <td className="w-40 px-3 py-2 text-meta">
                  {formatDate(documentStatus?.updatedAt ?? artifact?.updatedAt ?? null)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Review actions shown below the table when applicable */}
        {!isFinalized && !isReadOnly && (collaborationStatus === 'draft_complete' || collaborationStatus === 'in_review') && (
          <div className="mt-3">
            <ReviewActions
              collaborationStatus={collaborationStatus}
              reviewerId={documentAssignment?.reviewerId ?? null}
              bidMembers={bidMembers}
              currentUserId={currentUserId}
              isFinalized={isFinalized}
              onRequestReview={(reviewerId) => void handleRequestReview(reviewerId)}
              onSubmitReview={(decision, comment) => void handleSubmitReview(decision, comment)}
            />
          </div>
        )}

        {/* The AI's questions about this document (R1 Q2). */}
        {bidId && artifactId && (
          <DocumentQuestionsList bidId={bidId} artifactId={artifactId} className="mt-6" />
        )}
      </div>

      {/* Reopen Confirmation */}
      <AlertDialog
        open={showReopenDialog}
        onOpenChange={setShowReopenDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen {artifact?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The document will become editable again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleReopen()}
              disabled={isReopening}
            >
              {isReopening && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Reopen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BriefModal
        open={briefOpen}
        onOpenChange={setBriefOpen}
        bidId={bidId ?? ''}
        artifactId={artifactId ?? ''}
        sectionKey={DOCUMENT_SECTION_KEY}
        unitTitle={artifact?.name ?? 'Document'}
        scopeKind="artifact"
        canWrite={!isFinalized}
        onOpenEditor={() => {
          setBriefOpen(false)
          void navigate(`/app/bids/${bidId}/responses/${artifactId}/document`)
        }}
      />

      <ExportGapConfirmDialog {...exportGapDialogProps} />
    </div>
  )
}
